import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  X,
  LayoutDashboard,
  Brain,
  Settings,
  LogOut,
  Users,
  ListTree,
  BarChart2,
  FileSearch,
  BookOpen,
  CircleDollarSign,
  Globe,
  Menu,
  Plus,
  Shield,
} from 'lucide-react';
import SidebarContext from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import api from '../api/client';
import { formatCompactCount } from '../utils/formatNumber';
import CommandPalette from './CommandPalette';
import AppShellMobileNav from './AppShellMobileNav';
import packageJson from '../../package.json';
import './AppShell.css';
import '../styles/dashboardShell.css';
import '../styles/app-shell-matrix.css';

const GETTING_STARTED_STORAGE_KEY = 'subsecai_getting_started_dismissed';
const USAGE_WARN_80_KEY = 'subsecai_agent_usage_warn_80';
const ORG_EVENTS_WARN_80_KEY = 'subsecai_org_events_warn_80';
const ORG_EVENTS_WARN_100_KEY = 'subsecai_org_events_warn_100';

function userInitials(user) {
  if (!user?.email) return '?';
  const local = user.email.split('@')[0] || '';
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return local ? local.charAt(0).toUpperCase() : '?';
}

export default function AppShell({ children }) {
  const location = useLocation();
  const { showToast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(GETTING_STARTED_STORAGE_KEY) || 'false');
    } catch {
      return false;
    }
  });
  const { user, logout } = useAuth();
  const [orgUsage, setOrgUsage] = useState(null);

  const planSlug = (user?.plan || 'free').toLowerCase();
  const planBadgeLabel =
    planSlug === 'enterprise' ? 'Enterprise' : planSlug === 'team' ? 'Team' : 'Free plan';

  const sidebarContextValue = useMemo(
    () => ({
      openSidebar: () => setSidebarOpen(true),
      closeSidebar: () => setSidebarOpen(false),
    }),
    []
  );

  const closeSidebar = () => setSidebarOpen(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/chat/usage');
        if (cancelled) return;
        const u = res.data;
        if (!u || u.limit === -1 || u.plan === 'admin' || planSlug === 'enterprise') return;
        const pct = u.limit > 0 ? u.used / u.limit : 0;
        if (pct >= 0.8 && u.remaining > 0) {
          try {
            if (sessionStorage.getItem(USAGE_WARN_80_KEY)) return;
            sessionStorage.setItem(USAGE_WARN_80_KEY, '1');
          } catch (_) {
            /* sessionStorage may be unavailable */
          }
          showToast(
            `You have used about ${Math.min(100, Math.round(pct * 100))}% of today's Agent quota. Open Settings for plan options.`,
            'warning'
          );
        }
      } catch (_) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, showToast, planSlug]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/org/usage');
        if (cancelled) return;
        const o = res.data;
        setOrgUsage(o);
        if (!o || o.month_limit <= 0) return;

        if (o.month_remaining === 0) {
          try {
            if (!sessionStorage.getItem(ORG_EVENTS_WARN_100_KEY)) {
              sessionStorage.setItem(ORG_EVENTS_WARN_100_KEY, '1');
              showToast(
                "You've reached this month's exception ingest limit for your plan. Upgrade to raise the cap.",
                'warning'
              );
            }
          } catch (_) {
            /* sessionStorage unavailable */
          }
          return;
        }

        const pct = o.month_pct ?? (o.month_limit > 0 ? (o.month_used / o.month_limit) * 100 : 0);
        if (pct >= 80 && planSlug !== 'enterprise') {
          try {
            if (sessionStorage.getItem(ORG_EVENTS_WARN_80_KEY)) return;
            sessionStorage.setItem(ORG_EVENTS_WARN_80_KEY, '1');
          } catch (_) {
            /* ignore */
          }
          showToast(
            `About ${Math.round(pct)}% of this month's exception quota used. Upgrade for higher ingest limits.`,
            'warning'
          );
        }
      } catch (_) {
        if (!cancelled) setOrgUsage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, showToast, planSlug]);

  const dismissGettingStarted = () => {
    setGettingStartedDismissed(true);
    try {
      localStorage.setItem(GETTING_STARTED_STORAGE_KEY, 'true');
    } catch (_) {}
  };

  useKeyboardShortcuts(() => setCommandOpen((prev) => !prev));

  const workspaceSubtitle = user?.email
    ? user.email.length > 32
      ? `${user.email.slice(0, 30)}…`
      : user.email
    : 'Workspace';

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      <div className="app-shell">
        <div
          className={`sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`}
          onClick={closeSidebar}
          onKeyDown={(e) => e.key === 'Escape' && closeSidebar()}
          role="button"
          tabIndex={-1}
          aria-hidden="true"
        />
        <header className="app-chrome-header" role="banner">
          <div className="app-chrome-header-left">
            <button
              type="button"
              className="app-chrome-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
            >
              <Menu size={22} strokeWidth={2} aria-hidden="true" />
            </button>
            <Link to="/dashboard" className="app-chrome-brand">
              <span className="app-chrome-brand-accent">SubSec</span>
              <span className="app-chrome-brand-rest">AI</span>
            </Link>
            <nav className="app-chrome-nav" aria-label="Primary">
              <NavLink to="/dashboard" end>
                Dashboard
              </NavLink>
              <NavLink to="/reports">Reports</NavLink>
              {user?.role === 'admin' ? <NavLink to="/admin">Admin</NavLink> : null}
            </nav>
          </div>
          <div className="app-chrome-header-right">
            <Link
              to="/settings#billing"
              className="app-plan-badge"
              title="Plan and usage in Settings"
            >
              <span className="app-plan-badge-main">{planBadgeLabel}</span>
              {orgUsage && orgUsage.month_limit > 0 && (
                <span className="app-plan-badge-usage" aria-hidden="true">
                  {formatCompactCount(orgUsage.month_used)}/
                  {formatCompactCount(orgUsage.month_limit)} events/mo
                </span>
              )}
              {orgUsage && orgUsage.month_limit === -1 && (
                <span className="app-plan-badge-usage">
                  {formatCompactCount(orgUsage.month_used)} events/mo
                </span>
              )}
            </Link>
            {(planSlug === 'free' || planSlug === 'team') && (
              <Link to="/pricing" className="btn btn-primary btn-sm app-topbar-upgrade">
                Upgrade
              </Link>
            )}
            {planSlug === 'enterprise' && (
              <Link to="/settings#billing" className="btn btn-secondary btn-sm app-topbar-upgrade">
                Usage
              </Link>
            )}
            <Link
              to="/settings"
              className="app-chrome-avatar"
              title="Settings"
              aria-label="Account and settings"
            >
              {userInitials(user)}
            </Link>
          </div>
        </header>
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <Link to="/dashboard" className="sidebar-logo" onClick={closeSidebar}>
              <img src="/logo-mark.svg" alt="" aria-hidden="true" width={20} height={20} />
              <span>SubSecAI</span>
            </Link>
            <button type="button" className="sidebar-close" onClick={closeSidebar}>
              <X size={20} />
            </button>
          </div>
          <div className="sidebar-matrix-brand">
            <div className="sidebar-matrix-brand-icon-wrap">
              <div className="sidebar-matrix-brand-icon-inner">
                <Shield size={22} strokeWidth={2} aria-hidden="true" />
              </div>
            </div>
            <div>
              <p className="sidebar-matrix-brand-title">SubSecAI</p>
              <p className="sidebar-matrix-brand-sub">{workspaceSubtitle}</p>
            </div>
          </div>
          <nav className="sidebar-nav">
            <Link
              to="/dashboard"
              className={`sidebar-link ${location.pathname === '/dashboard' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <LayoutDashboard size={18} />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/graph"
              className={`sidebar-link ${location.pathname === '/graph' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <ListTree size={18} aria-hidden="true" />
              <span>Exception Flow Map</span>
            </Link>
            <Link
              to="/agent"
              state={{ newChat: true }}
              className={`sidebar-link ${location.pathname === '/agent' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Brain size={18} />
              <span>Agent</span>
            </Link>
            <Link
              to="/search"
              className={`sidebar-link ${location.pathname.startsWith('/search') ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <FileSearch size={18} aria-hidden="true" />
              <span>Exception Logs</span>
            </Link>
            <Link
              to="/reports"
              className={`sidebar-link ${location.pathname === '/reports' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <BarChart2 size={18} />
              <span>Reports</span>
            </Link>
            <Link
              to="/uptime"
              className={`sidebar-link ${location.pathname === '/uptime' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Globe size={18} aria-hidden="true" />
              <span>Ping &amp; Uptime Monitor</span>
            </Link>
            <Link
              to="/pricing"
              className={`sidebar-link ${location.pathname === '/pricing' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <CircleDollarSign size={18} />
              <span>Plans &amp; pricing</span>
            </Link>
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className={`sidebar-link ${location.pathname === '/admin' ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <Users size={18} />
                <span>Admin</span>
              </Link>
            )}
          </nav>
          <div className="sidebar-shortcuts-hint" aria-label="Keyboard shortcuts">
            <span className="sidebar-shortcut-item">Cmd+K Exception logs</span>
            <span className="sidebar-shortcut-item">Cmd+J New Agent chat</span>
            <span className="sidebar-shortcut-item">D Dashboard</span>
          </div>
          {!gettingStartedDismissed && (
            <div className="sidebar-getting-started">
              <span className="sidebar-getting-started-text">
                Get started: SDK, API key, or Agent.
              </span>
              <div className="sidebar-getting-started-actions">
                <Link to="/docs" className="btn btn-ghost btn-sm" onClick={closeSidebar}>
                  Docs
                </Link>
                <Link to="/settings" className="btn btn-ghost btn-sm" onClick={closeSidebar}>
                  Settings
                </Link>
                <Link to="/agent" className="btn btn-primary btn-sm" onClick={closeSidebar}>
                  <Brain size={14} /> Agent
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm sidebar-getting-started-dismiss"
                  onClick={dismissGettingStarted}
                  aria-label="Dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          )}
          <div className="sidebar-footer">
            <Link
              to="/docs"
              className={`sidebar-link ${location.pathname === '/docs' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <BookOpen size={18} />
              <span>Docs</span>
            </Link>
            <Link
              to="/settings"
              className={`sidebar-link ${location.pathname === '/settings' ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <Settings size={18} />
              <span>Settings</span>
            </Link>
            <button type="button" onClick={logout} className="sidebar-link">
              <LogOut size={18} /> Sign Out
            </button>
          </div>
          <div className="sidebar-status-panel">
            <p className="sidebar-status-build">SubSecAI v{packageJson.version}</p>
          </div>
        </aside>
        <main className="app-content">
          {orgUsage && orgUsage.month_limit > 0 && orgUsage.month_remaining === 0 && (
            <div className="app-org-limit-banner" role="alert">
              <span>
                Monthly exception ingest limit reached for your plan. New events may need an
                upgrade.
              </span>
              <Link to="/contact" className="app-org-limit-banner-link">
                Contact sales
              </Link>
              <Link to="/pricing" className="app-org-limit-banner-link">
                View plans
              </Link>
            </div>
          )}
          <div className="app-content-body">{children}</div>
        </main>
        <AppShellMobileNav />
        <Link
          to="/agent"
          state={{ newChat: true }}
          className="app-shell-fab"
          aria-label="New Agent chat"
          title="New Agent chat"
        >
          <Plus size={24} strokeWidth={2.25} aria-hidden="true" />
        </Link>
      </div>
      <CommandPalette isOpen={commandOpen} onClose={() => setCommandOpen(false)} />
    </SidebarContext.Provider>
  );
}
